import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { duplicarEvento } from '@/features/evento/eventoThunks';
import Modal from '@/components/Modals/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function DuplicarEvento({ eventoOriginal, uidEmpresa, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { infoEmpresa } = useSelector((state) => state.empresa);

  // Pre-rellenar con datos del evento original
  const [formData, setFormData] = useState({
    nombre: eventoOriginal?.nombre || '',
    aforo: eventoOriginal?.aforo || '',
    acceptCreation: false,
    usoEmbajadores: String(eventoOriginal?.useAmbassador ?? '0'),
    incentivo: String(eventoOriginal?.ambassadorIncentive || '10'),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const termsAndConditions = `
    TÉRMINOS Y CONDICIONES PARA LA CREACIÓN DE EVENTOS

    1. ACEPTACIÓN DE LOS TÉRMINOS
    Al crear un evento en nuestra plataforma, usted acepta estar sujeto a estos términos y condiciones. Si no está de acuerdo con alguno de estos términos, no debe utilizar nuestro servicio.

    2. RESPONSABILIDADES DEL ORGANIZADOR
    2.1 El organizador del evento es completamente responsable del contenido, la organización y la ejecución del evento.
    2.2 Debe proporcionar información veraz y actualizada sobre el evento.
    2.3 Es responsable de cumplir con todas las leyes y regulaciones locales aplicables.
    2.4 Debe respetar los derechos de autor y propiedad intelectual.

    3. CONTENIDO DEL EVENTO
    3.1 El contenido del evento no debe ser ofensivo, ilegal o inapropiado.
    3.2 No se permite contenido que promueva discriminación, violencia o actividades ilegales.
    3.3 Nos reservamos el derecho de remover eventos que violen estas políticas.

    4. CAPACIDAD Y AFORO
    4.1 El aforo declarado debe corresponder a la capacidad real del venue.
    4.2 El organizador debe respetar los límites de capacidad por seguridad.
    4.3 Debe contar con los permisos necesarios para el aforo declarado.

    5. POLÍTICAS DE CANCELACIÓN
    5.1 Los eventos pueden ser cancelados según nuestras políticas.
    5.2 En caso de cancelación, debe notificar con tiempo suficiente.
    5.3 Aplican términos específicos para reembolsos según el tipo de evento.

    6. LIMITACIÓN DE RESPONSABILIDAD
    6.1 La plataforma no se hace responsable por daños derivados del evento.
    6.2 El organizador mantiene toda la responsabilidad legal del evento.
    6.3 Nuestra responsabilidad se limita al uso de la plataforma tecnológica.

    7. MODIFICACIONES
    7.1 Nos reservamos el derecho de modificar estos términos en cualquier momento.
    7.2 Los cambios serán notificados a través de la plataforma.
    7.3 El uso continuado implica aceptación de los nuevos términos.

    8. TERMINACIÓN
    8.1 Podemos terminar el acceso a la plataforma por violación de términos.
    8.2 El organizador puede cancelar eventos según las políticas establecidas.

    9. LEY APLICABLE
    Estos términos se rigen por las leyes del país donde opera la plataforma.

    10. CONTACTO
    Para consultas sobre estos términos, puede contactarnos a través de nuestros canales oficiales.

    Al marcar la casilla de aceptación, confirma que ha leído, entendido y acepta todos estos términos y condiciones.
  `;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.nombre.trim()) {
      errors.nombre = 'El nombre del evento es obligatorio';
    }
    if (!formData.aforo || formData.aforo <= 0) {
      errors.aforo = 'El aforo debe ser un número mayor a 0';
    }
    if (!formData.acceptCreation) {
      errors.acceptCreation = 'Debe aceptar los términos y condiciones';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const usoEmbajadoresNum = parseInt(formData.usoEmbajadores);
    if (usoEmbajadoresNum === 2 && !(infoEmpresa && infoEmpresa.planActivo === 1)) {
      setShowPlanModal(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const ambassadorIncentive = usoEmbajadoresNum === 0 ? 0 : parseInt(formData.incentivo);

      const result = await dispatch(
        duplicarEvento({
          uidEmpresa,
          duplicarData: {
            uidEventoOriginal: eventoOriginal?.uid,
            nombre: formData.nombre.trim(),
            aforo: parseInt(formData.aforo),
            acceptCreation: formData.acceptCreation,
            useAmbassador: usoEmbajadoresNum,
            ambassadorIncentive,
          },
        })
      ).unwrap();

      setSuccessMessage(`Evento "${result.nombre}" duplicado exitosamente.`);

      setTimeout(() => {
        navigate(`/empresa/${uidEmpresa}/evento/${result.uid}`);
        if (onClose) onClose();
      }, 2000);
    } catch (error) {
      console.error('Error al duplicar evento:', error);
      setFormErrors((prev) => ({
        ...prev,
        general: typeof error === 'string' ? error : error?.message || 'Error al duplicar el evento',
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    formData.nombre.trim() && formData.aforo > 0 && formData.acceptCreation;

  return (
    <div className="mx-auto p-6 bg-aw-bgbluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green scrollbar-box thin h-[85dvh] overflow-y-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Duplicar Evento</h2>
        <p className="text-white">
          Se creará un nuevo evento basado en{' '}
          <span className="text-aw-green font-semibold">"{eventoOriginal?.nombre}"</span>.
          Puedes modificar los datos antes de crear.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Campo Nombre */}
        <div>
          <label htmlFor="nombre" className="block text-sm font-medium text-white mb-2">
            Nombre del Evento <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="nombre"
            name="nombre"
            value={formData.nombre}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-aw-green/75 placeholder:text-gray-500 text-white ${
              formErrors.nombre ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ingrese el nombre del evento"
          />
          {formErrors.nombre && (
            <p className="mt-1 text-sm text-red-600">{formErrors.nombre}</p>
          )}
        </div>

        {/* Campo Aforo */}
        <div>
          <label htmlFor="aforo" className="block text-sm font-medium text-white mb-2">
            Aforo <span className="text-red-500">*</span>
            <p className="text-xs text-aw-green/85 mt-1">
              Nota: El aforo representa la capacidad máxima del lugar donde se realizará el evento. Asegúrese de ingresar un número adecuado para garantizar la seguridad y comodidad de los asistentes.
            </p>
            <p className="text-xs text-red-400 mt-1">
              <b>Nota:</b> Este dato no podrá ser modificado posteriormente.
            </p>
          </label>
          <input
            type="number"
            id="aforo"
            name="aforo"
            value={formData.aforo}
            onChange={handleInputChange}
            min="1"
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-aw-green/75 placeholder:text-gray-500 text-white ${
              formErrors.aforo ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Capacidad máxima del lugar"
          />
          {formErrors.aforo && (
            <p className="mt-1 text-sm text-red-600">{formErrors.aforo}</p>
          )}
        </div>

        {/* Campo Embajadores */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            ¿Utilizará Embajadores? <span className="text-red-500">*</span>
            <p className="text-xs text-aw-green/85 mt-1">
              Los embajadores son personas que ayudarán en la venta de tickets de su evento. Seleccione la configuración de embajadores y el porcentaje de comisión que recibirán por cada venta.
            </p>
            <p className="text-xs text-red-400 mt-1">
              <b>Nota:</b> La comisión para los embajadores no podrá ser modificada después de crear el evento en este paso, asegúrese de seleccionar el porcentaje correcto si decide utilizar embajadores.
            </p>
          </label>

          <div className="mt-3 space-y-3">
            <div className="flex flex-col gap-2">
              <label className="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-aw-bluedark/50 transition-colors">
                <input
                  type="radio"
                  name="usoEmbajadores"
                  value="0"
                  checked={formData.usoEmbajadores === '0'}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-aw-green border-gray-300 focus:ring-aw-green/75"
                />
                <span className="ml-3 text-sm text-white">No utilizar embajadores</span>
              </label>

              <label className="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-aw-bluedark/50 transition-colors">
                <input
                  type="radio"
                  name="usoEmbajadores"
                  value="1"
                  checked={formData.usoEmbajadores === '1'}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-aw-green border-gray-300 focus:ring-aw-green/75"
                />
                <span className="ml-3 text-sm text-white">Todos los embajadores de AppWhoop</span>
              </label>

              <label className="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-aw-bluedark/50 transition-colors">
                <input
                  type="radio"
                  name="usoEmbajadores"
                  value="2"
                  checked={formData.usoEmbajadores === '2'}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-aw-green border-gray-300 focus:ring-aw-green/75"
                />
                <span className="ml-3 text-sm text-white">
                  Selección exclusiva de embajadores{' '}
                  {!(infoEmpresa && infoEmpresa.planActivo === 1) && (
                    <span className="text-xs">⭐</span>
                  )}
                </span>
              </label>
            </div>

            {(formData.usoEmbajadores === '1' || formData.usoEmbajadores === '2') && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-white mb-2">
                  Incentivo (Comisión para el embajador) <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-row items-center flex-wrap gap-2">
                  {/* 3, 5, 10, 15, 20 */}
                  {['3','5', '10', '15', '20'].map((val) => (
                    <label
                      key={val}
                      className="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-aw-bluedark/50 transition-colors"
                    >
                      <input
                        type="radio"
                        name="incentivo"
                        value={val}
                        checked={formData.incentivo === val}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-aw-green border-gray-300 focus:ring-aw-green/75"
                      />
                      <span className="ml-3 text-sm text-white">{val}%</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Términos y Condiciones */}
        <div>
          <h3 className="text-lg font-medium text-white">
            Términos y Condiciones <span className="text-red-500">*</span>
          </h3>
          <div className="flex items-start mt-4 p-4 bg-aw-bluedark rounded-lg border border-gray-700">
            <input
              type="checkbox"
              id="acceptCreation"
              name="acceptCreation"
              checked={formData.acceptCreation}
              onChange={handleInputChange}
              className="mt-0.5 h-4 w-4 text-aw-green border-gray-300 rounded focus:ring-aw-green/75"
            />
            <label htmlFor="acceptCreation" className="ml-3 text-sm text-white">
              Acepto los{' '}
              <button
                type="button"
                onClick={() => setShowTermsModal(true)}
                className="text-aw-green hover:text-aw-green/80 underline font-medium"
              >
                términos y condiciones
              </button>{' '}
              para crear el nuevo evento
            </label>
          </div>
          {formErrors.acceptCreation && (
            <p className="mt-2 text-sm text-red-600">{formErrors.acceptCreation}</p>
          )}
        </div>

        {/* Mensaje de éxito */}
        {successMessage && (
          <div className="p-4 bg-aw-green/10 border border-aw-green rounded-md">
            <p className="text-sm text-aw-green">{successMessage}</p>
          </div>
        )}

        {/* Mensaje de error general */}
        {formErrors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{formErrors.general}</p>
          </div>
        )}

        {/* Botones */}
        <div className="flex justify-end space-x-4 pt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-aw-green/75 duration-300 hover:scale-95"
            disabled={isSubmitting}
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={!isFormValid || isSubmitting}
            className={`px-6 py-2 rounded-md text-aw-bluedark focus:outline-none focus:ring-2 focus:ring-aw-green/75 flex items-center space-x-2 duration-300 ${
              !isFormValid || isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-aw-green hover:scale-95'
            }`}
          >
            {isSubmitting && <i className="fas fa-spinner animate-spin"></i>}
            <span>{isSubmitting ? 'Duplicando...' : 'Crear Nuevo Evento'}</span>
          </button>
        </div>
      </form>

      {/* Modal de plan requerido */}
      {showPlanModal && (
        <Modal
          isOpen={showPlanModal}
          onClose={() => setShowPlanModal(false)}
          large="max-w-2xl"
        >
          <div className="mx-auto p-6 bg-aw-bgbluedark rounded-lg text-center inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green">
            <FontAwesomeIcon
              icon="fa-solid fa-info-circle"
              className="text-aw-green text-5xl mb-4"
            />
            <h3 className="text-xl font-semibold text-white mb-4">
              Necesitas un plan activo
            </h3>
            <p className="text-gray-300 mb-6">
              La selección exclusiva de embajadores es una característica premium.
              Adquiere un plan para acceder a esta funcionalidad.
            </p>
            <button
              onClick={() => {
                setShowPlanModal(false);
                navigate(`/empresa/${uidEmpresa}`);
              }}
              className="w-full bg-aw-green hover:bg-aw-green/90 text-aw-bluedark font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Ir a Suscripción
            </button>
          </div>
        </Modal>
      )}

      {/* Modal de Términos y Condiciones */}
      <Modal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        large="max-w-3xl"
      >
        <div className="bg-aw-bgbluedark rounded-2xl p-6 inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green">
          <h2 className="text-2xl font-bold text-white mb-4">Términos y Condiciones</h2>
          <div className="max-h-[70vh] overflow-y-auto scrollbar-box thin">
            <div className="whitespace-pre-wrap text-sm text-gray-300">
              {termsAndConditions}
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => setShowTermsModal(false)}
              className="px-6 py-2 bg-aw-green text-aw-bluedark rounded-md font-medium hover:bg-aw-green/90 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
